import {addToast, Button, Dropdown, Text, Toggle} from '@albert-io/atomic';
import type {ReactNode} from 'react';
import React, {useEffect, useState} from 'react';
import {Link} from 'react-router';
import {useShallow} from 'zustand/react/shallow';

import {useCompareClassesStore} from 'client/AdaptiveSubjects/CompareClasses/CompareClasses.store';
import sessionStore from 'client/Session/SessionStore';
import {useFluxStore} from 'client/framework';
import {useUpdateClassroomSubjectAccessMutation} from 'gql/shared/ClassroomSubjectAccess.queries.generated';
import useMandarkQueries from 'lib/hooks/useMandarkQueries';

import {getClassroomsQuery} from './PracticeSettings.queries';

import './practice-settings.scss';

interface Props {
  subjectId: string;
  trigger: ReactNode;
  afterChanged?: () => void;
  className?: string;
  'data-testid'?: string;
}

export const PracticeSettings = ({
  subjectId,
  trigger,
  afterChanged,
  className,
  'data-testid': dataTestId
}: Props) => {
  'use memo';

  const userId = useFluxStore(sessionStore, (s) => s.getUserId());
  const classroomsQuery = getClassroomsQuery(userId, subjectId);
  const {isReady, resources} = useMandarkQueries([classroomsQuery]);
  const [loadingClassrooms, setLoadingClassrooms] = useState<Set<string>>(new Set());
  const [isEnablingAll, setIsEnablingAll] = useState(false);
  const [updateClassroomSubjectAccess] = useUpdateClassroomSubjectAccessMutation();

  const {setCompareClassesDisabled, setHasClasses} = useCompareClassesStore(
    useShallow((state) => ({
      setCompareClassesDisabled: state.setDisabled,
      setHasClasses: state.setHasClasses
    }))
  );

  const onChange = async () => {
    classroomsQuery.invalidateInterest();
    if (afterChanged) {
      afterChanged();
    }
    await classroomsQuery.getResourcePromise();
  };

  const [classrooms] = resources;

  const hasRestrictedClassroom = classrooms.some((classroom) =>
    classroomDoesNotHaveAccess(classroom, subjectId)
  );

  const hasAllRestrictedClassrooms = classrooms.every((classroom) =>
    classroomDoesNotHaveAccess(classroom, subjectId)
  );

  const hasClassrooms = !!classrooms.size;

  useEffect(() => {
    setHasClasses(hasClassrooms);
  }, [setHasClasses, hasClassrooms]);

  // set the disabled state for the compare classes toggle if empty classrooms
  useEffect(() => {
    if (hasAllRestrictedClassrooms) {
      setCompareClassesDisabled(true);
    } else {
      setCompareClassesDisabled(false);
    }
  }, [hasAllRestrictedClassrooms, setCompareClassesDisabled]);

  const enableAllClassrooms = async () => {
    setIsEnablingAll(true);

    try {
      const promises = classrooms
        .filter((classroom) => {
          return classroomDoesNotHaveAccess(classroom, subjectId);
        })
        .map(async (classroom) => {
          return updateClassroomSubjectAccess({
            variables: {
              classroomId: classroom.getId(),
              subjectId,
              input: {restrictFreePractice: false}
            }
          });
        });

      if (promises.size > 0) {
        const results = await Promise.all(promises);
        const failedResults = results.filter(
          (r) => r.data?.updateClassroomSubjectAccess?.errors?.length
        );
        if (failedResults.length > 0) {
          throw new Error('One or more classrooms failed to update');
        }
      }
      await onChange();
      addToast({
        message: 'Your changes have been saved.',
        title: 'Success!',
        color: 'positive'
      });
    } catch (_error) {
      addToast({
        message: 'There was a problem saving your changes. Please try again.',
        title: 'Error',
        color: 'negative'
      });
    } finally {
      setIsEnablingAll(false);
    }
  };

  if (!isReady) {
    return null;
  }

  return (
    <Dropdown
      trigger={trigger}
      position='bottom-end'
      // Fixed strategy escapes overflow clipping by scroll containers (the classwork subjects
      // pane), matching SubjectCourseCard's practice-settings dropdown.
      positionFixed
      className={className}
      data-testid={dataTestId}
    >
      {({close}) => (
        <div className='practice-settings__content-container' data-testid='practice-settings-modal'>
          {classrooms.size === 0 && (
            <div className='p-x2' data-testid='practice-settings-no-classes-message'>
              <div className='rounded-x1 background-subtle p-x1'>
                <Text color='primary'>
                  You can toggle on classroom access to this subject in this menu. Start by making a
                  class <Link to='/classes'>in your class dashboard</Link>.
                </Text>
              </div>
            </div>
          )}
          {classrooms.size > 0 && (
            <>
              <div className='practice-settings__info p-x2 flex cursor-default'>
                <Text className='text-left' color='primary'>
                  Choose which classes should have access to this subject:
                </Text>
              </div>
              <div className='p-x1'>
                {classrooms.map((classroom) => (
                  <ClassroomToggle
                    key={classroom.getId()}
                    classroom={classroom}
                    onChange={onChange}
                    subjectId={subjectId}
                    isLoading={loadingClassrooms.has(classroom.getId()) || isEnablingAll}
                    setIsLoading={(loading) => {
                      setLoadingClassrooms((prev) => {
                        const newSet = new Set(prev);
                        if (loading) {
                          newSet.add(classroom.getId());
                        } else {
                          newSet.delete(classroom.getId());
                        }
                        return newSet;
                      });
                    }}
                  />
                ))}
              </div>
            </>
          )}
          <div className='practice-settings__footer'>
            {classrooms.size > 1 && hasRestrictedClassroom && (
              <Button
                color='secondary'
                variant='text'
                size='s'
                onClick={enableAllClassrooms}
                disabled={isEnablingAll}
              >
                {isEnablingAll ? 'Enabling...' : 'Enable for all'}
              </Button>
            )}
            <Button
              color='primary'
              size='s'
              variant='solid'
              onClick={close}
              data-testid='practice-settings-close-btn'
            >
              {classrooms.size > 0 ? 'Done' : 'Close'}
            </Button>
          </div>
        </div>
      )}
    </Dropdown>
  );
};

interface ClassroomToggleProps {
  classroom: any;
  subjectId: string;
  onChange: () => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

const ClassroomToggle = ({
  classroom,
  onChange,
  isLoading,
  setIsLoading,
  subjectId
}: ClassroomToggleProps) => {
  const [updateClassroomSubjectAccess] = useUpdateClassroomSubjectAccessMutation();
  const subject = classroom.getSubjects().first();
  const [optimisticChecked, setOptimisticChecked] = useState<boolean | null>(null);

  const actualChecked =
    subject && !subject.getClassroomRelationships(classroom.getId()).getRestrictFreePractice();
  const checked = optimisticChecked !== null ? optimisticChecked : actualChecked;

  const toggle = async () => {
    const newCheckedState = !checked;
    setOptimisticChecked(newCheckedState);
    setIsLoading(true);

    try {
      // checked is actually the inverse of the current "restrict free practice" state
      const result = await updateClassroomSubjectAccess({
        variables: {
          classroomId: classroom.getId(),
          subjectId,
          input: {restrictFreePractice: !newCheckedState}
        }
      });

      const mutationErrors = result.data?.updateClassroomSubjectAccess?.errors;
      if (mutationErrors && mutationErrors.length > 0) {
        throw new Error(mutationErrors[0]?.message ?? 'Failed to update classroom subject access');
      }

      await onChange();
      addToast({
        message: 'Your changes have been saved.',
        title: 'Success!',
        color: 'positive'
      });
      setOptimisticChecked(null); // Clear optimistic state after successful update
    } catch (_error) {
      addToast({
        message: 'Error updating classroom access. Please try again.',
        color: 'negative',
        duration: 3000
      });
      setOptimisticChecked(null); // Revert optimistic state on error
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='gap-x2 px-x1-half py-x1 flex items-center justify-between'>
      <Text className='practice-settings__item-text' color='primary'>
        {classroom.getName()}
      </Text>
      <Toggle
        size='xs'
        label=''
        checked={checked}
        onChange={toggle}
        disabled={isLoading}
        data-testid={`practice-settings-${classroom.getName()}-toggle`}
      />
    </div>
  );
};

const classroomDoesNotHaveAccess = (classroom: any, subjectId: string) => {
  const subject = classroom.getSubjects().first();
  return (
    !subject ||
    subject.getClassroomRelationships(classroom.getId()).getRestrictFreePractice() ||
    subject.getId() !== subjectId
  );
};
